/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps, Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { useCallback, useRef } from 'preact/hooks';
import { FocusableHandle, useFocusableTextField } from '../hooks/UNSAFE_useFocusableTextField';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { useTextField } from '../hooks/UNSAFE_useTextField';
import { useHover } from '../hooks/UNSAFE_useHover';
import { Label } from '../UNSAFE_Label';
import {
  ReadonlyTextField,
  ReadonlyTextFieldInput,
  TextField,
  TextFieldInput
} from '../UNSAFE_TextField';
import {
  CompactUserAssistance,
  InlineUserAssistance,
  UserAssistanceDensityType
} from '../UNSAFE_UserAssistance';
import { RevealToggleIcon } from './RevealToggleIcon';
import { useClearIcon } from '../hooks/UNSAFE_useClearIcon';
import { beforeVNode } from '../utils/UNSAFE_componentUtils';
import { ClearIcon } from '../UNSAFE_InputText/ClearIcon';
import { Size } from '../utils/UNSAFE_size';
import { useToggle } from '../hooks/UNSAFE_useToggle';
import { useCurrentValueReducer } from '../hooks/UNSAFE_useCurrentValueReducer';
import { ValueUpdateDetail } from '../utils/UNSAFE_valueUpdateDetail';

type TextFieldInputProps = ComponentProps<typeof TextFieldInput>;
type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type Props = {
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the input.
   */
  'aria-describedby'?: TextFieldInputProps['aria-describedby'];

  /**
   * Text to provide guidance to help the user understand what to enter.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];
  /**
   * Dictates component's autocomplete state.
   * This attribute indicates whether the value of the control can be automatically completed by the browser.
   * The common values are "on" and "off".
   */
  autoComplete?: TextFieldInputProps['autoComplete'];
  /**
   * Autofocus specifies whether the component will get focus when the page is loaded.
   * If it is set to true then the associated component will get input focus when the page is loaded.
   * Setting this property doesn't set the focus to the component; it tells the browser to focus
   * to it when the element is inserted in the document.
   */
  autoFocus?: TextFieldInputProps['autoFocus'];
  /**
   * Specifies whether the clear icon should be shown.
   * <ul>
   * <li><code>'always'</code>: The clear icon is always visible.</li>
   * <li><code>'conditionally'</code>: The clear icon is visible under the following conditions:
   * if the component has a non-empty value, and it either has focus or the mouse is over the field.</li>
   * <li><code>'never'</code>: The clear icon is never visible.</li>
   * </ul>
   */
  hasClearIcon?: 'always' | 'conditionally' | 'never';
  /**
   * Specifies whether the reveal toggle should be shown.
   * <ul>
   * <li><code>'always'</code>: The reveal toggle is always visible and the user can click on it to reveal the password in plain text.
   * When the user leaves the field, the password is automatically masked.</li>
   * <li><code>'never'</code>: The reveal toggle is never visible and the user can never reveal the password in plain text.</li>
   * </ul>
   */
  hasRevealToggle?: 'always' | 'never';

  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
  /**
   * Custom text to be rendered for the <code>helpSourceLink</code>.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
  /**
   * Specifies whether the component is disabled.
   */
  isDisabled?: boolean;
  /**
   * Specifies whether the component is readonly.
   */
  isReadonly?: boolean;
  /**
   * Specifies for accessibility purposes whether a value is required.
   *
   * Setting this property to <code>false</code> means that a value is not required to be
   * committed by the user.  Setting this property to <code>true</code> means that a value
   * is required to be committed by the user.
   */
  isRequired?: TextFieldInputProps['isRequired'];
  /**
   * Specifies whether to show an indicator on screen that a value is required, for example
   * before the user has committed a value.
   */
  isRequiredShown?: InlineUserAssistanceProps['isRequiredShown'];
  /**
   * Specifies the label associated with the field.
   */
  label: string;
  /**
   * Specifies where the label is positioned relative to the field. If the labelEdge is 'none',
   * the labelHint is hidden from the user but is still accessible.
   */
  labelEdge?: 'inside' | 'start' | 'top' | 'none';
  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>
   */
  labelStartWidth?: Size;
  /**
   * Messages to show on screen that are associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];
  /**
   * A short hint to display before the user enters a value.
   */
  placeholder?: TextFieldInputProps['placeholder'];
  /**
   * Specifies how to align text within the field.
   */
  textAlign?: TextFieldInputProps['textAlign'];
  /**
   * Specifies the density of the user assistance presentation.  It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'reflow'</code>: Show inline.  Layout will reflow when text is displayed.</li>
   * <li><code>'compact'</code>: Messages, help, hints, and required will not be shown inline; they will show in a mode that keeps the screen more compact, like a popup for the messages, and a required icon to indicate Required. </li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;
  /**
   * The current display value of the component.
   */
  value?: TextFieldInputProps['value'];
  /**
   * The style variant of the component.
   */
  variant?: TextFieldInputProps['variant'];
  /**
   * Callback invoked when the user commits the entered value by either blurring or pressing Enter.
   *
   */
  onCommit?: TextFieldInputProps['onCommit'];

  /**
   * Callback invoked each time the user changes the value of the field. For example, if the user
   * types 'abcd', this callback will be called four times with the value as 'a', 'ab', 'abc' and 'abcd'
   * respectively.
   */
  onInput: TextFieldInputProps['onInput'];
};

// One way InputPassword differs from InputText is InputPassword's readonly
// is implemented with an <input> and not a <div>.
export const InputPassword = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      autoComplete = 'off',
      autoFocus = false,
      hasClearIcon,
      hasRevealToggle = 'always',
      helpSourceLink,
      helpSourceText,
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      isRequired = false,
      isRequiredShown,
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      messages,
      placeholder,
      textAlign: propTextAlign,
      userAssistanceDensity: propUserAssistanceDensity,
      value,
      variant = 'default',
      onInput,
      onCommit
    }: Props,
    ref?: Ref<FocusableHandle>
  ) => {
    const { currentCommitValue, dispatch } = useCurrentValueReducer({ value });
    const onInputAndDispatch = useCallback(
      (detail: ValueUpdateDetail<string>) => {
        // Should dispatch happen first? This will queue up a re-render, ordering should not cause issues (this is async)
        dispatch({ type: 'input', payload: detail.value });
        onInput?.(detail);
      },
      [onInput, dispatch]
    );
    const onCommitAndDispatch = useCallback(
      (detail: ValueUpdateDetail<string>) => {
        // Should dispatch happen first? This will queue up a re-render, ordering should not cause issues (this is async)
        dispatch({ type: 'commit', payload: detail.value });
        onCommit?.(detail);
      },
      [onCommit, dispatch]
    );
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      textAlign: formTextAlign,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();
    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    const textAlign = propTextAlign ?? formTextAlign;
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;
    const {
      bool: isRevealed,
      setFalse: setRevealedFalse,
      setTrue: setRevealedTrue
    } = useToggle(false);
    const { enabledElementRef, focusProps, isFocused, readonlyElementRef } = useFocusableTextField<
      HTMLInputElement,
      HTMLInputElement
    >({ isDisabled, isReadonly, ref, onBlurWithin: setRevealedFalse });
    const { hoverProps, isHover } = useHover({ isDisabled: isReadonly || isDisabled || false });
    const { formFieldContext, inputProps, labelProps, textFieldProps, userAssistanceProps } =
      useTextField({
        ariaDescribedBy,
        helpSourceLink,
        helpSourceText,
        isDisabled,
        isFocused,
        isReadonly,
        isRequiredShown,
        labelEdge,
        messages,
        styleVariant: variant,
        userAssistanceDensity,
        value
      });

    // Callback on the RevealToggleButton.
    // When the user clicks on the reveal buttton,
    // toggle revealing/masking password
    const onRevealIconPress = useCallback(() => {
      isRevealed ? setRevealedFalse() : setRevealedTrue();
    }, [isRevealed, setRevealedFalse, setRevealedTrue]);

    const revealToggleIcon =
      !isDisabled && hasRevealToggle === 'always' ? (
        <RevealToggleIcon onPress={onRevealIconPress} isRevealed={isRevealed} />
      ) : null;
    const onClickClearIcon = useCallback(() => {
      // Clicking the clear icon should put the focus on the input field
      enabledElementRef.current?.focus();
      // Send an event to clear the field's value
      onInputAndDispatch?.({ previousValue: value, value: '' });
    }, [onInputAndDispatch, value, enabledElementRef]);

    const maybeClearIcon = useClearIcon({
      clearIcon: <ClearIcon onClick={onClickClearIcon} />,
      display: hasClearIcon,
      hasValue: formFieldContext.hasValue,
      isFocused,
      isEnabled: !isReadonly && !isDisabled,
      isHover
    });
    const endContentCombined = beforeVNode(revealToggleIcon, maybeClearIcon);

    const labelComp = labelEdge !== 'none' ? <Label {...labelProps}>{label}</Label> : undefined;

    const fieldLabelProps = {
      label: labelEdge !== 'none' ? labelComp : undefined,
      labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
      labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined
    };

    const ariaLabel = labelEdge === 'none' ? label : undefined;
    const isInlineDensity =
      userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow';

    const inlineUserAssistance = isInlineDensity ? (
      isDisabled || isReadonly ? (
        // save space for user assistance if density is 'efficient', even though we don't
        // render user assistance for disabled or readonly fields
        userAssistanceDensity !== 'efficient' ? undefined : (
          <InlineUserAssistance
            userAssistanceDensity={userAssistanceDensity}
            {...userAssistanceProps}
          />
        )
      ) : (
        <InlineUserAssistance
          assistiveText={assistiveText}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          messages={messages}
          isRequiredShown={isRequiredShown}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      )
    ) : undefined;

    const anchorRef = useRef<HTMLDivElement>(null);

    const compactUserAssistance =
      userAssistanceDensity === 'compact' ? (
        <CompactUserAssistance
          anchorRef={anchorRef}
          messages={messages}
          assistiveText={assistiveText}
          {...userAssistanceProps}
        />
      ) : undefined;

    if (isReadonly) {
      return (
        <FormFieldContext.Provider value={formFieldContext}>
          <ReadonlyTextField
            role="presentation"
            compactUserAssistance={compactUserAssistance}
            inlineUserAssistance={inlineUserAssistance}
            ref={anchorRef}
            {...fieldLabelProps}>
            <ReadonlyTextFieldInput
              aria-describedby={inputProps['aria-describedby']}
              aria-label={ariaLabel}
              aria-labelledby={labelProps.id}
              as="input"
              autoFocus={autoFocus}
              elementRef={readonlyElementRef}
              textAlign={textAlign}
              type="password"
              value={value}
              hasEmptyLabel={label === '' && labelEdge === 'none'}
              hasInsideLabel={label !== undefined && labelEdge === 'inside'}
            />
          </ReadonlyTextField>
        </FormFieldContext.Provider>
      );
    }

    const mainContent = (
      <TextFieldInput
        aria-label={ariaLabel}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        currentCommitValue={currentCommitValue}
        hasEmptyLabel={label === '' && labelEdge === 'none'}
        hasInsideLabel={labelComp !== undefined && labelEdge === 'inside'}
        inputRef={enabledElementRef}
        isRequired={isRequired}
        onInput={onInputAndDispatch}
        onCommit={onCommitAndDispatch}
        placeholder={placeholder}
        textAlign={textAlign}
        value={value}
        type={isRevealed ? 'text' : 'password'}
        {...inputProps}
      />
    );

    return (
      <FormFieldContext.Provider value={formFieldContext}>
        <TextField
          endContent={endContentCombined}
          inlineUserAssistance={inlineUserAssistance}
          compactUserAssistance={compactUserAssistance}
          mainContent={mainContent}
          onBlur={focusProps.onfocusout}
          onFocus={focusProps.onfocusin}
          mainFieldRef={anchorRef}
          {...textFieldProps}
          {...fieldLabelProps}
          {...hoverProps}
        />
      </FormFieldContext.Provider>
    );
  }
);
